import { trackEvent } from "~/utils/monitoring";

// Analytics for the nextgen Chat experience. Event names are deliberately
// shared with the legacy Dataset Assistant (`components/assistant` and the
// dataset assistant-v2 wrapper) so a single Mixpanel dashboard can pivot both
// surfaces on the `context` property: nextgen events set `context: "nextgen"`
// where the dataset assistant sets `context: "dataset"`.
export const NEXTGEN_CHAT_CONTEXT = "nextgen";

// The identifiers a nextgen chat event carries when they are in scope. All
// optional so a navigation-level site (e.g. a starter-prompt click that
// predates any conversation) can emit with only what it has.
export interface NextgenChatEventContext {
  conversationId?: string | null;
  projectId?: string | null;
  agentId?: string | null;
  agentName?: string | null;
}

// Wire names for nextgen chat + project events. A chat-funnel action that has a
// legacy Dataset Assistant twin reuses that event name, so both surfaces land in
// one Mixpanel event separated by `context`; a nextgen-only concept (projects,
// sharing) takes a `nextgen_*` name, matching the existing `nextgen_connector_toggled`.
export const NEXTGEN_CHAT_EVENT = {
  CONVERSATION_OPENED: "assistant_conversation_selected",
  NEW_CHAT: "assistant_new_chat_clicked",
  CONVERSATION_DELETED: "assistant_conversation_deleted",
  CONVERSATION_RENAMED: "assistant_conversation_renamed",
  CONVERSATION_SHARED: "nextgen_conversation_shared",
  MESSAGE_SENT: "analysis_agent_message_sent",
  MESSAGE_RECEIVED: "analysis_agent_message_received",
  MESSAGE_ERROR: "analysis_agent_message_error",
  MESSAGE_CANCELLED: "analysis_agent_message_cancelled",
  PRESET_CLICKED: "analysis_agent_preset_clicked",
  PROJECT_OPENED: "nextgen_project_opened",
  PROJECT_CREATED: "nextgen_project_created",
} as const;
export type NextgenChatEvent = (typeof NEXTGEN_CHAT_EVENT)[keyof typeof NEXTGEN_CHAT_EVENT];

// Emit a nextgen chat analytics event, stamping `context: "nextgen"` and any
// in-scope ids so every call site reports a consistent shape. Absent ids are
// dropped rather than sent as null so the Mixpanel event stays clean. Extra
// per-event properties are merged on top.
export const trackNextgenChatEvent = (
  event: NextgenChatEvent,
  context: NextgenChatEventContext,
  properties: Record<string, unknown> = {}
): void => {
  trackEvent(event, {
    context: NEXTGEN_CHAT_CONTEXT,
    ...(context.conversationId ? { conversation_id: context.conversationId } : {}),
    ...(context.projectId ? { project_id: context.projectId } : {}),
    ...(context.agentId ? { agent_id: context.agentId } : {}),
    ...(context.agentName ? { agent_name: context.agentName } : {}),
    ...properties,
  });
};
